import { useCallback, useMemo, useState } from 'react';
import { LangResources, useTranslate } from '@providers/TranslateProvider';

import { ChessBoardProps } from '@exports/ChessBoard';
import { SquareName } from '@libs/chess/types';
import { useImmediateEffect } from '@libs/hooks/useImmediateEffect';

import { components } from '@features/shared/api/game';

const TEXTS = {
  draw: '@features/game/hooks/useScoresheet/Draw',
  whiteWin: '@features/game/hooks/useScoresheet/White Win',
  blackWin: '@features/game/hooks/useScoresheet/Black Win',
  whiteNoShow: '@features/game/hooks/useScoresheet/White NoShow',
  blackNoShow: '@features/game/hooks/useScoresheet/Black NoShow',
  whiteBan: '@features/game/hooks/useScoresheet/White Ban',
  blackBan: '@features/game/hooks/useScoresheet/Black Ban',
  whiteRemoved: '@features/game/hooks/useScoresheet/White Removed',
  blackRemoved: '@features/game/hooks/useScoresheet/Black Removed',
} satisfies Record<string, keyof LangResources>;

type PropsPatch = Partial<ChessBoardProps> & { position: string };

export type MappedProps = {
  moves: MoveMinimal[];
  onNavigate: (next: MoveMinimal) => void;
  selected?: MoveMinimal;
  result?: string;
  events?: Map<number, string[]>;
};

export type Value = {
  props: MappedProps;
  propsPatch?: PropsPatch;
  playersTimeMs?: { white?: number; black?: number };
  isLastMove?: boolean;
  reset: () => void;
};

type Params = {
  game?: GameMinimal;
  boardProps?: ChessBoardProps;
  autoResetOnMove?: boolean;
};

// Берём сущность и оставляем только используемые данные
type GameMinimal = {
  id: string;
  moves: MoveMinimal[];
  result: components['schemas']['GameResult'];
  lossReason?: components['schemas']['LossReason'];
  activeDrawRequest?: components['schemas']['ActiveDrawRequestDto'];
  gameEvents?: components['schemas']['GameEventDto'][] | null;
};
type MoveMinimal = {
  san: string;
  from: string;
  to: string;
  fenAfter: string;
  clockAfter?: {
    whiteLeftMs: number;
    blackLeftMs: number;
  };
};

export function useScoresheet({ game, boardProps, autoResetOnMove = true }: Params): Value {
  const { t } = useTranslate();
  const [selected, setSelected] = useState<MoveMinimal>();
  const reset = useCallback(() => {
    setSelected(undefined);
  }, []);
  const moves = useMemo(() => game?.moves ?? [], [game]);
  const events = useMemo(() => mapGameEventsToDrawRequests(game?.gameEvents), [game?.gameEvents]);

  const onNavigate = useCallback((next: MoveMinimal) => {
    setSelected(next);
  }, []);

  // сбросить выбор если случился новый ход в игре
  useImmediateEffect(() => {
    if (!selected) return;

    const wasLastMove = selected.fenAfter === moves[moves.length - 2]?.fenAfter;
    if (wasLastMove || autoResetOnMove) {
      setSelected(undefined);
    } else {
      // Привязать selected к новому массиву ходов (сохранить ===)
      const remapped = moves.find(
        (m) => m.fenAfter === selected.fenAfter && m.from === selected.from,
      );
      if (remapped && remapped !== selected) {
        setSelected(remapped);
      }
    }
  }, [game?.id, game?.moves.length, game?.result === 'InProgress']);

  const isLastMove = selected && selected === game?.moves[game?.moves.length - 1];
  const propsPatch: PropsPatch | undefined = selected
    ? isLastMove
      ? undefined
      : {
          position: selected.fenAfter,
          selectedFrom: selected.from as SquareName,
          selectedTo: selected.to as SquareName,
          interactive: 'none',
          onSquarePointerDown: undefined,
          winner: undefined,
        }
    : undefined;

  const playersTimeMs = selected
    ? {
        white: selected.clockAfter?.whiteLeftMs,
        black: selected.clockAfter?.blackLeftMs,
      }
    : undefined;

  return {
    props: {
      moves,
      onNavigate,
      selected,
      result:
        mapServerResult(game?.result, game?.lossReason, t) ??
        mapClientResult(boardProps?.winner, t) ??
        undefined,
      events,
    },
    propsPatch,
    playersTimeMs,
    isLastMove,
    reset,
  };
}

function mapServerResult(
  result: components['schemas']['GameResult'] | undefined,
  lossReason: components['schemas']['LossReason'] | undefined,
  t: ReturnType<typeof useTranslate>['t'],
): string | null {
  switch (result) {
    case 'InProgress':
    case 'NotStarted':
    case 'Unknown':
    case undefined:
      return null;
    case 'NotPlayed':
      return 'Партия отменена';
    case 'BlackWin':
      return mapLossReason(lossReason, 'white', t) ?? t(TEXTS.blackWin);
    case 'WhiteWin':
      return mapLossReason(lossReason, 'black', t) ?? t(TEXTS.whiteWin);
    case 'Draw':
      return t(TEXTS.draw);
  }
}

function mapLossReason(
  lossReason: components['schemas']['LossReason'] | undefined,
  loserSide: 'white' | 'black',
  t: ReturnType<typeof useTranslate>['t'],
): string | null {
  if (!lossReason) return null;

  if (loserSide === 'white') {
    switch (lossReason) {
      case 'NoShow':
        return t(TEXTS.whiteNoShow);
      case 'Ban':
        return t(TEXTS.whiteBan);
      case 'Removed':
        return t(TEXTS.whiteRemoved);
    }
  } else {
    switch (lossReason) {
      case 'NoShow':
        return t(TEXTS.blackNoShow);
      case 'Ban':
        return t(TEXTS.blackBan);
      case 'Removed':
        return t(TEXTS.blackRemoved);
    }
  }
}

function mapClientResult(
  result: ChessBoardProps['winner'],
  t: ReturnType<typeof useTranslate>['t'],
): string | null {
  switch (result) {
    case undefined:
      return null;
    case 'black':
      return t(TEXTS.blackWin);
    case 'white':
      return t(TEXTS.whiteWin);
    case 'draw':
      return t(TEXTS.draw);
  }
}

function mapGameEventsToDrawRequests(
  gameEvents: components['schemas']['GameEventDto'][] | null | undefined,
): Map<number, string[]> | undefined {
  if (!gameEvents?.length) return undefined;

  const filtered = gameEvents.filter(
    (e) => e.eventType === 'DrawRequest' || e.eventType === 'ClaimVictory',
  );
  if (!filtered.length) return undefined;

  const result = new Map<number, string[]>();

  for (const event of filtered) {
    // sic! в API FullMoveNumber — это HalfMoveNumber (aka ply), а не полных ход
    const fullMoveNumber = Math.ceil(event.fullMoveNumber / 2);
    const text =
      event.eventType === 'DrawRequest'
        ? event.triggeredBySide === 'White'
          ? 'Белые предложили ничью'
          : 'Черные предложили ничью'
        : event.triggeredBySide === 'White'
          ? 'Белые заявили победу'
          : 'Черные заявили победу';

    const existing = result.get(fullMoveNumber);
    if (existing) {
      existing.push(text);
    } else {
      result.set(fullMoveNumber, [text]);
    }
  }

  return result.size > 0 ? result : undefined;
}
